/** @module utils/numberFormat */

/**
 * Intl.NumberFormat options to set a number to integer format
 * @private
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#fractiondigits_significantdigits_and_integerdigits
 * @example 1.00 => 1
 */
const INTEGER_FORMAT_OPTIONS: Pick<
    Intl.NumberFormatOptions,
    'minimumFractionDigits' | 'maximumFractionDigits'
> = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
};

/**
 * Intl.NumberFormat options to set a number to decimal format
 * @private
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#fractiondigits_significantdigits_and_integerdigits
 * @example 1 => 1.00
 */
const DECIMAL_FORMAT_OPTIONS: Pick<
    Intl.NumberFormatOptions,
    'minimumFractionDigits' | 'maximumFractionDigits'
> = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
};

/**
 * Intl.NumberFormat options to set a number to USD currency
 * @private
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#currency_formatting
 * @example 1 => $1.00
 */
const USD_FORMAT_OPTIONS: Pick<Intl.NumberFormatOptions, 'style' | 'currency'> =
    {
        style: 'currency',
        currency: 'USD',
    };

/**
 * Intl.NumberFormat options to set a number format to 'compact'
 * @private
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#scientific_engineering_or_compact_notations
 * @example 1000000 => 1M
 */
const COMPACT_FORMAT_OPTIONS: Pick<Intl.NumberFormatOptions, 'notation'> = {
    notation: 'compact',
};

/**
 * Creates a number formatting function using the Intl.NumberFormat API
 * @private
 * @param options - Options for formatting numbers
 * @returns A function that formats a number using Intl.NumberFormat
 *
 * @example
 * const format = formatter({ minimumFractionDigits: 2 });
 * format(123.456); // "123.46"
 */
const formatter = (
    options: Intl.NumberFormatOptions
): ((number: number) => string) => {
    const numberFormatter = new Intl.NumberFormat('en-US', options);

    return (number: number): string => numberFormatter.format(number);
};

/**
 * Formats a number as decimal currency
 * @param number - The number to format
 * @returns The formatted number as a string
 *
 * @example
 * asDecimalCurrency(1); // "$1.00"
 */
export const asDecimalCurrency = formatter({
    ...USD_FORMAT_OPTIONS,
    ...DECIMAL_FORMAT_OPTIONS,
});

/**
 * Formats a number as integer currency
 * @param number - The number to format
 * @returns The formatted number as a string
 *
 * @example
 * asIntegerCurrency(1); // "$1"
 */
export const asIntegerCurrency = formatter({
    ...USD_FORMAT_OPTIONS,
    ...INTEGER_FORMAT_OPTIONS,
});

/**
 * Formats a number as compact currency
 * @param number - The number to format
 * @returns The formatted number as a string
 *
 * @example
 * asCompactCurrency(1000000); // "$1M"
 */
export const asCompactCurrency = formatter({
    ...USD_FORMAT_OPTIONS,
    ...COMPACT_FORMAT_OPTIONS,
});

/**
 * Formats a number with two decimal places
 * @param number - The number to format
 * @returns The formatted number as a string
 *
 * @example
 * asDecimal(1); // "1.00"
 */
export const asDecimal = formatter(DECIMAL_FORMAT_OPTIONS);

/**
 * Formats a number with two decimal places and no grouping
 * @param number - The number to format
 * @returns The formatted number as a string
 *
 * @example
 * asDecimalInteger(1000); // "1000.00"
 */
export const asDecimalInteger = formatter({
    ...DECIMAL_FORMAT_OPTIONS,
    useGrouping: false,
});

/**
 * Formats a number with no decimal places
 * @param number - The number to format
 * @returns The formatted number as a string
 *
 * @example
 * asInteger(1.00); // "1"
 */
export const asInteger = formatter(INTEGER_FORMAT_OPTIONS);

/**
 * Formats a number in compact notation
 * @param number - The number to format
 * @returns The formatted number as a string
 *
 * @example
 * asCompact(1000000); // "1M"
 */
export const asCompact = formatter(COMPACT_FORMAT_OPTIONS);

type FormatPriceOptions<T = string> = {
    formatterCallback?: (value: number) => T;
    parseValue?: boolean;
    toCents?: boolean;
    allowNull?: boolean;
};

/**
 * Generic price formatting utility and parser
 * @param value - The value to format, either a number or string
 * @param config - Options for formatting
 * @param config.formatterCallback - The formatting function to use
 * @param config.toCents - Whether to convert the value to cents (1.00 => 100)
 * @param config.parseValue - Whether to parse the value (remove currency symbols)
 * @param config.allowNull - Whether to return null for null/undefined/empty string values
 * @returns The formatted value as a string or number
 *
 * @example
 * formatPrice(1234.56, { toCents: true }); // 123456
 * formatPrice("$1000.00", { parseValue: true }); // "1,000.00"
 * formatPrice(null, { allowNull: true }); // null
 * formatPrice("", { allowNull: true }); // null
 */
export const formatPrice = <T = string>(
    value: number | string | null | undefined,
    {
        formatterCallback = asDecimal as (value: number) => T,
        parseValue = false,
        toCents = false,
        allowNull = false,
    }: FormatPriceOptions<T> = {}
): T | null => {
    // eslint-disable-next-line no-undefined
    if (allowNull && (value === null || value === undefined || value === '')) {
        return null;
    }

    if (parseValue) {
        const parsedValue = parseFloat(String(value).replace(/[^\d.-]/g, ''));

        return formatterCallback(parsedValue);
    }

    if (toCents) {
        return formatterCallback(Number((Number(value) * 100).toFixed(0)));
    }

    return formatterCallback(Number(value) / 100);
};

/**
 * Generic price masking utility for input normalization
 * @param value - The value to normalize
 * @returns A normalized string containing only valid price characters
 *
 * @example
 * maskPrice("123.456abc"); // "123.45"
 */
export const maskPrice = (value: string): string => {
    const pendingValue = value
        // Replace starting period with '0.'
        .replace(/^\./, '0.')
        // Remove all non-numeric and non-period characters
        .replace(/[^0-9.]/g, '')
        // Remove all but the first period
        .replace(/([.].*?)[.]/, '$1');

    const [dollars, cents] = pendingValue.split('.');

    // If cents exist, return up to two decimal places
    return cents
        ? [dollars, cents.substring(0, 2)].filter(Boolean).join('.')
        : pendingValue;
};

/**
 * Generic percentage masking utility for input normalization
 * @param value - The value to normalize
 * @returns A normalized string containing only valid percentage characters
 *
 * @example
 * maskPercentage("123.456abc"); // "123.45"
 */
export const maskPercentage = (value: string): string => {
    const pendingValue = value.replace(/[^0-9.]/g, ''); // Remove all non-numeric and non-period characters

    return pendingValue;
};

/**
 * Parses a currency string into an integer number of cents (pennies)
 * @param currencyString - The currency string to parse (may include currency symbols and grouping separators)
 * @returns The parsed value as an integer number of cents
 *
 * @example
 * parseCurrencyToCents("$18.81"); // 1881
 * parseCurrencyToCents("$1,000.00"); // 100000
 * parseCurrencyToCents(".2"); // 20
 */
export const parseCurrencyToCents = (currencyString: string): number => {
    // [^\d\.] matches any character not a digit or a period
    const valueString = currencyString.replace(/[^\d.]/g, '');

    return Math.round(parseFloat(valueString) * 100);
};
